import type { PathContainer, PathContainerElement, PathSegment, Separator } from '../../../types/path';

export function subPath(container: PathContainer, start: number, end?: number): PathContainer {
    const elements = container.elements;
    end = end ?? elements.length;
    if (start === 0 && end === elements.length) {
        return container;
    }
    if (start === end) {
        return DefaultPathContainer.EMPTY_PATH;
    }
    if (!(start >= 0 && start < elements.length)) throw new Error('start index out of bounds: ' + start);
    if (!(start >= 0 && end <= elements.length)) throw new Error('end index out of bounds: ' + end);
    if (!(start < end)) throw new Error('start index must be less than end index: ' + start + ' >= ' + end);
    const slice = elements.slice(start, end);
    const path = slice.map(e => e.value).join('');
    return new DefaultPathContainer(path, slice);
}

export function isSeparator(element: PathContainerElement): element is Separator {
    return element.value.length === 1 && element['valueToMatch'] === undefined && element['parameters'] === undefined;
}

export function isPathSegment(element: PathContainerElement): element is PathSegment {
    return !isSeparator(element);
}

export type Options = {
    readonly separator: number; // char code 0x2f for '/' and 0x2e for '.'
    readonly decodeAndParseParameters: boolean;
}
export const HTTP_OPTIONS: Options = {
    separator: '/'.charCodeAt(0),
    decodeAndParseParameters: true,
}

export const MESSAGING_OPTIONS: Options = {
    separator: '.'.charCodeAt(0),
    decodeAndParseParameters: false,
}

export function parsePath(path: string, options?: Options): PathContainer {
    return DefaultPathContainer.createFromUrlPath(path, options ?? HTTP_OPTIONS);
}

type DefaultSeparator = Separator & { readonly encoded: string };

class DefaultPathContainer implements PathContainer {
    static readonly EMPTY_PATH: PathContainer = new DefaultPathContainer('', []);
    static readonly #SEPARATORS: Map<number, DefaultSeparator> = new Map([
        ['/'.charCodeAt(0), { value: '/', encoded: '%2F' }],
        ['.'.charCodeAt(0), { value: '.', encoded: '%2E' }],
    ]);

    readonly #path: string;
    readonly #elements: readonly PathContainerElement[];

    constructor(path: string, elements: PathContainerElement[]) {
        this.#path = path;
        this.#elements = Object.freeze(elements);
    }

    get value(): string {
        return this.#path;
    }

    get elements(): readonly PathContainerElement[] {
        return this.#elements;
    }

    static createFromUrlPath(path: string, options: Options): PathContainer {
        if (path === '') {
            return DefaultPathContainer.EMPTY_PATH;
        }
        const separator = options.separator;
        const separatorElement = DefaultPathContainer.#SEPARATORS.get(separator);
        if (separatorElement === undefined) {
            throw new Error('Unsupported separator: ' + separator);
        }

        const elements: PathContainerElement[] = [];
        let start: number;
        if (path.charCodeAt(0) === separator) {
            start = 1;
            elements.push(separatorElement);
        }
        else {
            start = 0;
        }

        while (start < path.length) {
            let end = -1;
            for (let i = start; i < path.length; i++) {
                if (path.charCodeAt(i) === separator) {
                    end = i;
                    break;
                }
            }
            const segment = (end !== -1) ? path.substring(start, end) : path.substring(start);

            if (segment.length > 0) {
                elements.push(options.decodeAndParseParameters ?
                    DefaultPathContainer.#decodeAndParsePathSegment(segment) :
                    DefaultPathSegment.from(segment, separatorElement)
                );
            }
            if (end === -1) {
                break;
            }
            elements.push(separatorElement);
            start = end + 1;
        }
        return new DefaultPathContainer(path, elements);
    }

    static #decodeAndParsePathSegment(segment: string): PathSegment {
        const paramsIndex = segment.indexOf(';');
        if (paramsIndex === -1) {
            const valueToMatch = decodeURIComponent(segment);
            return DefaultPathSegment.from(segment, valueToMatch);
        }
        else {
            const valueToMatch = decodeURIComponent(segment.substring(0, paramsIndex));
            const pathParameterContent = segment.substring(paramsIndex);
            const parameters = DefaultPathContainer.#parsePathParameters(pathParameterContent);
            return DefaultPathSegment.from(segment, valueToMatch, parameters);
        }
    }

    static #parsePathParameters(content: string): Map<string, readonly string[]> {
        const parameters: Map<string, string[]> = new Map<string, string[]>();
        let start = 1; // skip initial ';'
        while (start < content.length) {
            let end = -1;
            for (let i = start; i < content.length; i++) {
                if (content.charAt(i) === ';') {
                    end = i;
                    break;
                }
            }
            const parameter = (end !== -1) ? content.substring(start, end) : content.substring(start);
            DefaultPathContainer.#parsePathParameterValue(parameter, parameters);
            if (end === -1) {
                break;
            }
            start = end + 1;
        }
        if (parameters.size > 0) {
            // freeze all value arrays and the map itself for runtime immutability
            for (const values of parameters.values()) {
                Object.freeze(values);
            }
        }
        return parameters;
    }

    static #parsePathParameterValue(input: string, output: Map<string, string[]>): void {
        if (input) {
            const eqIndex = input.indexOf('=');
            if (eqIndex !== -1) {
                const name = decodeURIComponent(input.substring(0, eqIndex));
                if (name.trim()) {
                    const value = input.substring(eqIndex + 1);
                    for (const v of value.split(',')) {
                        const values = output.get(name);
                        if (values !== undefined) {
                            values.push(decodeURIComponent(v));
                        }
                        else {
                            output.set(name, [decodeURIComponent(v)]);
                        }
                    }
                }
            }
            else {
                const name = decodeURIComponent(input);
                if (name.trim()) {
                    const values = output.get(name);
                    if (values !== undefined) {
                        values.push('');
                    }
                    else {
                        output.set(name, ['']);
                    }
                }
            }
        }
    }
}

class DefaultPathSegment implements PathSegment {
    static readonly #EMPTY_PARAMS: ReadonlyMap<string, readonly string[]> = new Map<string, readonly string[]>();

    static from(value: string, separatorOrValueToMatch: string | DefaultSeparator, parameters?: Map<string, readonly string[]>): PathSegment {
        if (typeof separatorOrValueToMatch === 'string') {
            const valueToMatch = separatorOrValueToMatch;
            return new DefaultPathSegment(value, valueToMatch, parameters);
        }
        else {
            const separator = separatorOrValueToMatch;
            const valueToMatch = (value.includes(separator.encoded)) ? value.replaceAll(separator.encoded, separator.value) : value;
            return DefaultPathSegment.from(value, valueToMatch, parameters);
        }
    }

    readonly #value: string;
    readonly #valueToMatch: string;
    readonly #parameters: ReadonlyMap<string, readonly string[]>;

    constructor(value: string, valueToMatch: string, parameters: ReadonlyMap<string, readonly string[]> = DefaultPathSegment.#EMPTY_PARAMS) {
        this.#value = value;
        this.#valueToMatch = valueToMatch;
        this.#parameters = parameters;
    }

    get value(): string {
        return this.#value;
    }

    get valueToMatch(): string {
        return this.#valueToMatch;
    }

    get parameters(): ReadonlyMap<string, readonly string[]> {
        return this.#parameters;
    }

}
